import * as React from "react";
import { Badge, BadgeProps } from "components/ui/badge";
import { QueryStatusEnum } from "@/enum/queryStatus";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "components/ui/textarea";
import { ClipboardCopy } from "lucide-react";
import { DynamicTable } from "@/pages/it/updates-control/list/components/edit/dynamic-table";

export function QueryModal({ query }: { query: any }) {
  const [copied, setCopied] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [editedQuery] = React.useState(query.query || "");

  const QueryStatusLabels: Record<QueryStatusEnum, string> = {
    [QueryStatusEnum.AUTO_APPROVED]: "Aprovado Automaticamente",
    [QueryStatusEnum.APPROVED]: "Aprovado",
    [QueryStatusEnum.PENDING]: "Pendente",
    [QueryStatusEnum.REPROVED]: "Reprovado",
  };

  const queryStatusConfig: Record<
    QueryStatusEnum,
    { text: string; variant: BadgeProps["variant"] }
  > = {
    [QueryStatusEnum.APPROVED]: { text: "Aprovado", variant: "approved" },
    [QueryStatusEnum.PENDING]: { text: "Pendente", variant: "pending" },
    [QueryStatusEnum.REPROVED]: { text: "Reprovado", variant: "reproved" },
    [QueryStatusEnum.AUTO_APPROVED]: {
      text: "Aprovado Automaticamente",
      variant: "autoApproved",
    },
  };

  const getStatusLabel = (status: QueryStatusEnum | undefined): string => {
    if (!status) return "Desconhecido";
    return QueryStatusLabels[status] || "Desconhecido";
  };

  const statusConfig = queryStatusConfig[query?.status as QueryStatusEnum];

  const handleCopy = () => {
    navigator.clipboard.writeText(editedQuery);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Badge
          className={`hover:bg-opacity-80 cursor-pointer`}
          variant={statusConfig.variant}
        >
          {getStatusLabel(query?.status)}
        </Badge>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[565px] lg:max-w-[700px] bg-white">
        <DialogHeader>
          <DialogTitle>Visualizar Query</DialogTitle>
          <DialogDescription>Sua query está aqui.</DialogDescription>
        </DialogHeader>
        <div className="w-full">
          <div className="flex gap-2">
            <div className="relative w-full max-w-lg">
              <Textarea
                className="font-consolas min-h-40 w-full bg-[#F9FAFB] border-black/20 border rounded-lg px-3 py-2 focus:outline-none focus:border-2 focus:border-[#000000]/40 disabled:cursor-default disabled:opacity-100 resize-none"
                value={editedQuery}
                disabled
              />
              <div className="absolute bottom-2 right-2 flex items-center space-x-2">
                {copied && (
                  <p className="text-sm text-[#fff] bg-black px-2 py-1 rounded">
                    Copiado
                  </p>
                )}
                <button
                  className="bg-[#000000] text-white px-3 py-1 rounded hover:bg-[#000000]/80 transition-all ease-in-out duration-300"
                  onClick={handleCopy}
                >
                  <ClipboardCopy size={20} />
                </button>
              </div>
            </div>
            <div className="w-full">
              <Textarea
                className="font-consolas min-h-40 w-full bg-[#fff] border-black/20 border rounded-lg px-3 py-2 focus:outline-none focus:border-2 focus:border-[#000000]/40 disabled:cursor-default disabled:opacity-100 resize-none"
                value={query?.approval_log?.feedback || "Sem Feedbacks  "}
                disabled
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col w-full gap-2">
          <label htmlFor="" className="text-sm">
            Itens que serão modificados:
          </label>
          <DynamicTable data={query?.result_before} />
        </div>
        <DialogFooter>
          <DialogClose>
            <button className="bg-transparent border border-black/20 text-black rounded-md font-semibold text-sm p-2 px-4 flex items-center gap-2 hover:bg-black/10">
              Sair
            </button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
